import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Code2, Terminal, User, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LanguageSelector from "@/components/LanguageSelector";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useTranslation();

  const navLinks = [
    { path: "/", label: t('nav.home') },
    { path: "/urunler", label: t('nav.products') },
    { path: "/portfolyo", label: t('nav.portfolio') },
    { path: "/hakkimizda", label: t('nav.about') },
    { path: "/iletisim", label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-effect" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00d4aa] to-[#0ea5e9] flex items-center justify-center group-hover:animate-pulse-glow transition-all">
              <Code2 className="w-6 h-6 text-[#0a0a0f]" />
            </div>
            <div className="flex flex-col">
              <span className="font-mono font-bold text-lg text-[#f0f0f5] tracking-tight">
                BF Code Labs
              </span>
              <span className="text-[10px] text-[#9ca3af] font-mono hidden sm:block">
                {'// ' + t('hero.badge')}
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link font-medium text-sm ${isActive(link.path) ? 'active' : ''}`}
                data-testid={`nav-${link.path === '/' ? 'home' : link.path.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Language Selector */}
            <LanguageSelector />
            
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                {user?.is_admin && (
                  <Link
                    to="/admin"
                    className={`nav-link font-medium text-sm flex items-center gap-1 ${isActive('/admin') ? 'active' : ''}`}
                    data-testid="nav-admin"
                  >
                    {t('nav.admin')}
                  </Link>
                )}
                <Link
                  to="/dashboard"
                  className={`nav-link font-medium text-sm flex items-center gap-1 ${isActive('/dashboard') ? 'active' : ''}`}
                  data-testid="nav-dashboard"
                >
                  <User className="w-4 h-4" />
                  {user?.name?.split(' ')[0]}
                </Link>
                <button
                  onClick={logout}
                  className="p-2 text-[#9ca3af] hover:text-[#00d4aa] transition-colors"
                  title={t('nav.logout')}
                  data-testid="nav-logout"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Link
                to="/giris"
                className="btn-primary text-sm flex items-center gap-2"
                data-testid="nav-login"
              >
                <Terminal className="w-4 h-4" />
                {t('nav.login')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#f0f0f5]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-16 left-0 right-0 glass-effect border-t border-[#2a2a3a] transition-all duration-300 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        data-testid="mobile-menu"
      >
        <div className="px-4 py-4 space-y-3">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block py-2 px-4 rounded-lg font-medium transition-colors ${
                isActive(link.path)
                  ? 'bg-[#00d4aa]/10 text-[#00d4aa]'
                  : 'text-[#9ca3af] hover:text-[#f0f0f5] hover:bg-[#16161f]'
              }`}
              onClick={() => setIsOpen(false)}
              data-testid={`mobile-nav-${link.path === '/' ? 'home' : link.path.slice(1)}`}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Mobile Language Selector */}
          <LanguageSelector isMobile={true} />
          
          {isAuthenticated ? (
            <>
              {user?.is_admin && (
                <Link
                  to="/admin"
                  className="block py-2 px-4 rounded-lg font-medium text-[#f97316] bg-[#f97316]/10"
                  onClick={() => setIsOpen(false)}
                >
                  {t('nav.admin')} Panel
                </Link>
              )}
              <Link
                to="/dashboard"
                className="block py-2 px-4 rounded-lg font-medium text-[#00d4aa] bg-[#00d4aa]/10"
                onClick={() => setIsOpen(false)}
              >
                <User className="w-4 h-4 inline mr-2" />
                {t('nav.dashboard')}
              </Link>
              <button
                onClick={() => { logout(); setIsOpen(false); }}
                className="block w-full text-left py-2 px-4 rounded-lg font-medium text-[#9ca3af] hover:bg-[#16161f]"
              >
                <LogOut className="w-4 h-4 inline mr-2" />
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <Link
              to="/giris"
              className="block btn-primary text-center mt-4"
              onClick={() => setIsOpen(false)}
              data-testid="mobile-nav-login"
            >
              {t('nav.login')}
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
